import { useEffect, useState } from "react";
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useParams,
  useRouterState,
} from "@tanstack/react-router";
import { FlowStrip } from "@/components/pixel";
import { Button } from "@/components/ui";
import { authClient } from "@/lib/auth";
import { BASEPATH } from "@/lib/redirect";
import { meQueryOptions, useMe, type Me } from "@/lib/session";
import { ACCENTS, BRAND } from "@postman/core/theme";
import type { VerticalSlug } from "@postman/core/types";
import { VERTICALS } from "@postman/core/verticals";

const NAMES = new Map(VERTICALS.map((v) => [v.slug as string, v.name]));

/**
 * The session guard.
 *
 * A pathless layout, so every route file nested under `_auth/` inherits it
 * without opting in — the failure mode of a per-route guard is the route
 * someone forgot, and this shape has no such route. It resolves the session
 * through the query cache, so the component tree below reads the same object
 * the guard checked rather than fetching it again.
 *
 * This is convenience, not security. The API refuses the same requests on its
 * own, per request, against the database. Deleting this file would make the
 * portal ugly, not permissive.
 */
export const Route = createFileRoute("/_auth")({
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions);
    if (!me) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
    return { me };
  },
  component: Shell,
});

const navLink =
  "mono text-ink-3 transition-colors hover:text-ink [&.active]:text-ink";
const activeProps = { className: "active" };

function Shell() {
  const me = useMe();
  // Every route with a vertical spells the param `vertical`, so one loose read
  // colours the band for all of them. `strict: false` because most routes do
  // not have it, and the absence is the brand-orange case rather than an error.
  const params = useParams({ strict: false }) as { vertical?: string };
  const slug = params.vertical;
  const accent =
    slug && slug in ACCENTS ? ACCENTS[slug as VerticalSlug] : BRAND;

  return (
    <div className="flex min-h-dvh flex-col">
      <Nav me={me} />
      {/* One band per screen, and it takes the colour of whatever vertical you
          are looking at. The LAW stays `shutter` — see the note on FlowStrip;
          a radial law in a 12:1 strip leaves most of it empty. */}
      <FlowStrip accent={accent} className="h-[120px] w-full border-b border-line" />
      <main className="shell flex-1 py-12 md:py-16">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function Nav({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  // Close on navigation. A menu that survives the tap that used it covers the
  // page you just asked for, and on a phone that reads as the link failing.
  const href = useRouterState({ select: (s) => s.location.href });
  useEffect(() => setOpen(false), [href]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  async function signOut() {
    await authClient.signOut();
    // A hard navigation, deliberately, and not `navigate({ to: "/login" })`.
    //
    // Two reasons, and the first is a bug I actually hit: a client-side
    // navigation runs the login route's guard against a query cache that is
    // still holding the signed-out person's session, so login decides you are
    // signed in and bounces you straight back to the dashboard. Clearing the
    // cache first only trades that for a render of `useMe()` against nothing.
    //
    // The second is the reason to keep it even once that is worked around:
    // a document load is the only thing that guarantees no fragment of one
    // person's profile — cached queries, router state, a component's own
    // useState — survives into the next person's session at this browser.
    window.location.href = `${BASEPATH}/login`;
  }

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-paper/90 backdrop-blur">
      <div className="shell flex h-14 items-center justify-between gap-6">
        <div className="flex items-center gap-6">
          <Link to="/dashboard" className="mono">
            LABS<span className="text-[color:var(--accent)]">/</span>RECRUIT
          </Link>
          {/* The fixed links are written out rather than generated from a
              list: `to` is a typed literal, so a route that gets renamed or
              removed fails the build here instead of turning into a dead link.
              The coordinator links are generated because their number is a
              fact about the signed-in person, not about the app. */}
          <nav className="hidden items-center gap-5 lg:flex">
            <Links me={me} />
          </nav>
        </div>

        <div className="flex items-center gap-3">
          <span className="mono hidden text-ink-3 md:inline">
            {me.user.bitsId ?? me.user.email}
          </span>
          <Button
            variant="ghost"
            className="hidden h-9 px-3 lg:inline-flex"
            onClick={signOut}
          >
            Sign out
          </Button>

          {/* The menu button exists only where the row above is hidden, so
              there is never a moment with two ways to reach the same link. */}
          <button
            type="button"
            aria-expanded={open}
            aria-controls="portal-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            className="mono flex h-9 w-9 items-center justify-center border border-line lg:hidden"
            onClick={() => setOpen((o) => !o)}
          >
            <span aria-hidden>{open ? "✕" : "≡"}</span>
          </button>
        </div>
      </div>

      {/* Rendered only when open rather than hidden with a class: a closed
          menu that is still in the tree is still in the tab order, and the
          first Tab from the logo lands on a link nobody can see. */}
      {open ? (
        <nav
          id="portal-menu"
          className="flex flex-col gap-1 border-t border-line px-5 py-4 lg:hidden"
        >
          <Links me={me} stacked />
          <span className="mono mt-3 border-t border-line pt-3 text-ink-3">
            {me.user.bitsId ?? me.user.email}
          </span>
          <Button variant="ghost" className="mt-2 h-11 self-start px-4" onClick={signOut}>
            Sign out
          </Button>
        </nav>
      ) : null}
    </header>
  );
}

/**
 * Every link in the nav, once.
 *
 * Written out rather than generated from a list: `to` is a typed literal, so
 * a route that gets renamed or removed fails the build here instead of
 * turning into a dead link. The coordinator links ARE generated, because
 * their number is a fact about the signed-in person rather than about the app.
 *
 * `stacked` is the mobile arrangement — the same links, at a tap target, with
 * the group headings kept because a coordinator of three verticals needs to
 * know which list is which.
 */
function Links({ me, stacked = false }: { me: Me; stacked?: boolean }) {
  const item = stacked ? `${navLink} py-2 text-[15px]` : navLink;
  const rule = stacked
    ? "mt-2 flex flex-col gap-1 border-t border-line pt-3"
    : "flex items-center gap-4 border-l border-line pl-5";

  return (
    <>
      <Link to="/dashboard" className={item} activeProps={activeProps}>
        Dashboard
      </Link>
      <Link to="/apply" className={item} activeProps={activeProps}>
        Apply
      </Link>
      <Link to="/onboarding" className={item} activeProps={activeProps}>
        Profile
      </Link>

      {/* `coordinates` is re-derived from the grant table on every /me, so a
          revoked coordinator loses this link on their next request — and
          would be refused by the API even if they kept it. */}
      {me.coordinates.length > 0 ? (
        <span className={rule}>
          <span className="mono text-ink-3">Coordinating</span>
          {me.coordinates.map((slug) => (
            <Link
              key={slug}
              to="/coord/$vertical"
              params={{ vertical: slug }}
              className={item}
              activeProps={activeProps}
            >
              {NAMES.get(slug) ?? slug}
            </Link>
          ))}
        </span>
      ) : null}

      {me.user.role === "admin" ? (
        <Link
          to="/admin"
          className={stacked ? `${item} mt-2 border-t border-line pt-3` : `${navLink} border-l border-line pl-5`}
          activeProps={activeProps}
        >
          Admin
        </Link>
      ) : null}
    </>
  );
}

function Footer() {
  return (
    <footer className="border-t border-line">
      <div className="shell flex h-16 items-center justify-between">
        <p className="mono text-ink-3">Rakesh Kapoor Innovation Centre</p>
        <a
          href="/"
          className="mono text-ink-3 transition-colors hover:text-ink"
        >
          postmanlabs
        </a>
      </div>
    </footer>
  );
}
